/**
 * API Client Configuration
 *
 * Always use `apiClient` instead of raw `axios` for API calls.
 *
 * The apiClient automatically:
 * - Adds the `Authorization: Bearer <access-token>` header from the
 *   in-memory token store (see ../auth/accessToken).
 * - Sends the HTTP-only refresh cookie (`withCredentials: true`) so the
 *   server can rotate the session on /auth/refresh.
 * - On a 401 response, transparently attempts ONE refresh and replays
 *   the original request. If the refresh itself fails the user is
 *   logged out and redirected to /login.
 *
 * Usage:
 *   import { apiClient } from '../services/api';
 *   const response = await apiClient.get('/endpoint');
 *
 * The only exception is the auth endpoints in AuthContext (`/auth/login`,
 * `/auth/refresh`, `/auth/logout`) which talk to the server with raw
 * axios because they manage the session state this interceptor depends on.
 */
import axios, { AxiosError, AxiosRequestConfig } from 'axios';

import { API_BASE_URL } from '../config/api';
import { getAccessToken, setAccessToken, clearAccessToken } from '../auth/accessToken';

export const apiClient = axios.create({
  baseURL: API_BASE_URL,
  timeout: 30000,
  // Must be true so the browser includes the HTTP-only refresh cookie
  // on every request. Without this the cookie wouldn't travel and the
  // automatic 401-recovery below would fail on its very first refresh.
  withCredentials: true,
  headers: {
    'Content-Type': 'application/json',
  },
});

apiClient.interceptors.request.use(config => {
  const token = getAccessToken();
  if (token) {
    config.headers.Authorization = `Bearer ${token}`;
  }
  return config;
});

// ---------------------------------------------------------------------
// Auto-refresh on 401
// ---------------------------------------------------------------------
//
// When the backend returns 401 the access JWT has likely just expired
// (15 min TTL in prod). We try to mint a new one by posting to
// /auth/refresh — the HTTP-only refresh cookie is sent automatically —
// and on success retry the original request with the fresh token.
//
// Three guards keep this from misbehaving:
//   1. Per-request `_retry` flag — never refresh twice for the same
//      request, even if the retried call also returns 401.
//   2. Single in-flight refresh promise — concurrent 401s from a wave
//      of parallel requests all wait on the same refresh, instead of
//      each kicking off their own and racing.
//   3. Skip refresh for the auth endpoints themselves — those are the
//      ones that own the refresh flow, and looping through this
//      interceptor would deadlock.
// ---------------------------------------------------------------------

let refreshInFlight: Promise<string | null> | null = null;

function isAuthEndpoint(url?: string): boolean {
  if (!url) {
    return false;
  }
  return (
    url.includes('/auth/login') || url.includes('/auth/refresh') || url.includes('/auth/logout')
  );
}

async function performRefresh(): Promise<string | null> {
  if (refreshInFlight) {
    return refreshInFlight;
  }

  refreshInFlight = (async () => {
    try {
      const response = await axios.post(
        `${API_BASE_URL}/auth/refresh`,
        {},
        { withCredentials: true }
      );
      const minted = response.data?.accessToken ?? response.data?.token;
      if (typeof minted === 'string' && minted.length > 0) {
        setAccessToken(minted);
        return minted;
      }
      return null;
    } catch {
      return null;
    } finally {
      // Always clear so the next 401 starts a fresh refresh attempt.
      refreshInFlight = null;
    }
  })();

  return refreshInFlight;
}

apiClient.interceptors.response.use(
  response => response,
  async (error: AxiosError) => {
    const status = error.response?.status;
    const original = error.config as (AxiosRequestConfig & { _retry?: boolean }) | undefined;

    if (status !== 401 || !original || original._retry || isAuthEndpoint(original.url)) {
      return Promise.reject(error);
    }

    original._retry = true;
    const newToken = await performRefresh();
    if (!newToken) {
      // Refresh failed — drop session state and bounce to login. We
      // avoid the redirect when we're already on the login page so the
      // login attempt's own 401 doesn't cause a navigation flicker.
      clearAccessToken();
      if (typeof window !== 'undefined' && !window.location.pathname.startsWith('/login')) {
        window.location.href = '/login';
      }
      return Promise.reject(error);
    }

    original.headers = {
      ...(original.headers || {}),
      Authorization: `Bearer ${newToken}`,
    };
    return apiClient.request(original);
  }
);
